import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { Grid, makeStyles, Typography } from "@material-ui/core";

import { ActionTimeGroup } from "../../../../components/actionTimeTimeGroup";
import { ShaDowBox } from "../../../../components/boxes/ShadowBox";
import { COLORS } from "../../../../theme/colors";
import { GRAPH_UNITS } from "../../../dashboard/constants";
import { Modal } from "../../../../components/base/Modal";
import { ApproveAssetsModal } from "./ApproveAssetsModal";
import { getData } from "../../../../services/api";
import { nFormatter, currencyFormatter, nKilowattFormatter } from "../../../../services";
import { ProductionTotal } from "../../../powerAssets/tabs/overview/ProductionTotal";
import { InstalledCapacity } from "../../../powerAssets/tabs/overview/InstalledCapacity";
import { ProductionOverTime } from "../../../powerAssets/tabs/overview/ProductionOverTime";

const useStyles = makeStyles((theme) => ({
  root: {
    paddingBottom: theme.spacing(4),
  },
  timeRangeText: {
    textTransform: "uppercase",
    marginRight: theme.spacing(2),
    fontWeight: "bold",
  },
  box: {
    padding: theme.spacing(2),
  },
  smallKWhText: {
    lineHeight: 2.2,
    marginLeft: theme.spacing(0.5),
    fontWeight: 300,
  },
  title: {
    fontWeight: "bold",
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(2),
  },
  topPanel: {
    marginBottom: theme.spacing(3),
  },
}));

export const PowerTab = () => {
  const classes = useStyles();
  const currencySymbol = useSelector((state) => state.auth.user.currency.symbol);
  const UNIT_OPTIONS = [currencySymbol, "kWh"];
  const [unit, setUnit] = useState(UNIT_OPTIONS[1]);
  const [approveAssetsModal, setApproveAssetsModal] = useState(false);
  const [powerInfoData, setPowerInfoData] = useState();
  const { period, startDate, endDate } = useSelector((state) => state.selectedPeriods);
  const { cluster, segments, selectedCountries } = useSelector((state) => state.selectCSC);

  useEffect(() => {
    const dateQueryParams = {
      period,
      startDate,
      endDate,
    };
    getData("/praas/v1/buildings/power-info/", setPowerInfoData, dateQueryParams);
  }, [period, startDate, endDate, cluster, segments, selectedCountries]);

  return (
    <div className={classes.root}>
      {/* Time selection, action buttons, toggle button */}
      <Grid container alignItems="center" justifyContent="space-between">
        <ActionTimeGroup
          hideIntegrateBtn
          showToggleFilter
          toggleFilter={unit}
          setToggleFilter={setUnit}
        />
      </Grid>

      <ShaDowBox>
        <Grid container className={classes.topPanel}>
          {[
            {
              title: "Total Batteries",
              value: powerInfoData ? nFormatter(powerInfoData.battery) : 0,
              capacity: powerInfoData
                ? GRAPH_UNITS.kwh === unit
                  ? nKilowattFormatter(powerInfoData.battery_kwh)
                  : currencyFormatter(powerInfoData.battery_capacity, currencySymbol)
                : 0,
            },
            {
              title: "Total EV",
              value: powerInfoData ? nFormatter(powerInfoData.ev) : 0,
              capacity: powerInfoData
                ? GRAPH_UNITS.kwh === unit
                  ? nKilowattFormatter(powerInfoData.ev_kwh)
                  : currencyFormatter(powerInfoData.ev_capacity, currencySymbol)
                : 0,
            },
            {
              title: "Total solar sys.",
              value: powerInfoData ? nFormatter(powerInfoData.solar) : 0,
              capacity: powerInfoData
                ? GRAPH_UNITS.kwh === unit
                  ? nKilowattFormatter(powerInfoData.solar_kwh)
                  : currencyFormatter(powerInfoData.solar_capacity, currencySymbol)
                : 0,
            },
            {
              title: "New Power Asset Recommendations",
              assets: 4,
              buildings: 2,
              capacity: 100,
              unrealized: 34,
            },
          ].map((entry, index) => (
            <Grid
              className={classes.box}
              key={index}
              item
              xs={index === 3 ? 6 : 2}
              style={{
                borderLeft: index === 0 ? "none" : `1px solid ${COLORS.darks[0][10]}`,
              }}
              id={entry.title.toLowerCase().replaceAll(" ", "-").replaceAll(".", "")}
            >
              <Typography>{entry.title}</Typography>
              <Typography variant="h3">{entry.value}</Typography>
              {entry.assets && (
                <Typography component="span" variant="h3" style={{ color: "red" }}>
                  {entry.assets} Assets{" "}
                  <Typography component="span">(in {entry.buildings} buildings)</Typography>
                </Typography>
              )}
              <Typography>
                <Typography
                  component="span"
                  style={{
                    paddingRight: `5px`,
                  }}
                >
                  Total Capacity: {entry.capacity}
                </Typography>
                {entry.unrealized && (
                  <Typography
                    component="span"
                    style={{
                      borderLeft: `1px solid ${COLORS.darks[0][10]}`,
                      paddingRight: `5px`,
                      paddingLeft: `5px`,
                      color: "red",
                    }}
                  >
                    Unrealized Rev: ${entry.unrealized}K/mo
                  </Typography>
                )}
              </Typography>
            </Grid>
          ))}
        </Grid>
      </ShaDowBox>

      {/* charts */}
      <Grid container spacing={3}>
        <Grid item xs={4}>
          <ProductionTotal
            endpoint="/praas/v1/buildings/power-total/"
            period={period}
            startDate={startDate}
            endDate={endDate}
            graphUnit={unit}
          />
        </Grid>

        <Grid item xs={4}>
          <InstalledCapacity
            endpoint="/praas/v1/buildings/power-capacity/"
            period={period}
            startDate={startDate}
            endDate={endDate}
            graphUnit={unit}
          />
        </Grid>

        <Grid item xs={4}>
          <ProductionOverTime
            endpoint="/praas/v1/buildings/production-time/"
            period={period}
            startDate={startDate}
            endDate={endDate}
            showByFilter={false}
            graphUnit={unit}
          />
        </Grid>
      </Grid>

      <Modal open={approveAssetsModal} onClose={() => setApproveAssetsModal(false)}>
        <ApproveAssetsModal
          onClose={() => setApproveAssetsModal(false)}
          assets={[
            {
              type: "solar",
              name: "Asset name",
              address: "Landmark Office Space,, Canary Wharf",
            },
          ]}
        />
      </Modal>
    </div>
  );
};
